import { httpsCallable } from 'firebase/functions';
import { ref, uploadBytesResumable } from 'firebase/storage';

import { auth, functions, storage } from './clients';
import { trackPendingOperation } from '../../services/pendingOperations';
import type { NexusGateway } from '../../domain/nexus/api';
import {
  GOOGLE_NEXUS_MAX_ARCHIVE_BYTES,
  newGoogleNexusUploadId,
  type GoogleNexusConnectionResult,
  type GoogleNexusDashboard,
  type GoogleNexusProjectInput,
  type GoogleNexusRefreshResult,
  type GoogleNexusSyncResult,
  type GoogleNexusUpload,
  type GoogleNexusValidationInput,
  type GoogleNexusValidationResult,
  type NexusBugDetail,
  type NexusBugEditor,
  type NexusBugList,
  type NexusBugMetadata,
  type NexusBugReportResult,
  type NexusCsvExport,
  type NexusDeliveredTasksResult,
  type NexusDeliveryOverview,
  type NexusProjectDetail,
  type StagedGoogleNexusArchive,
} from '../../domain/nexus/model';

interface NexusActionRequest {
  action: string;
  [key: string]: unknown;
}

async function callGoogleNexus<T>(request: NexusActionRequest): Promise<T> {
  const callable = httpsCallable<NexusActionRequest, T>(functions, 'googleNexus');
  const response = await callable(request);
  return response.data;
}

function safeArchiveName(name: string): string {
  const safe = name.replace(/[^A-Za-z0-9._-]+/g, '_').replace(/^\.+/, '');
  return safe || 'nexus-world.tar';
}

async function stageArchive(
  uid: string,
  uploadId: string,
  archive: File,
  onProgress?: (bytesTransferred: number, totalBytes: number) => void,
): Promise<StagedGoogleNexusArchive> {
  if (archive.size > GOOGLE_NEXUS_MAX_ARCHIVE_BYTES) {
    throw new Error(`Archive is over the ${GOOGLE_NEXUS_MAX_ARCHIVE_BYTES}-byte limit.`);
  }
  const archiveName = safeArchiveName(archive.name);
  const storagePath = `googleNexusUploads/${uid}/${uploadId}/${archiveName}`;
  const upload = uploadBytesResumable(ref(storage, storagePath), archive, {
    contentType: archive.type || 'application/x-tar',
    customMetadata: {
      originalName: archive.name,
      uploadedByUid: uid,
      nexusUploadId: uploadId,
    },
  });

  await new Promise<void>((resolve, reject) => {
    upload.on(
      'state_changed',
      (snapshot) => onProgress?.(snapshot.bytesTransferred, snapshot.totalBytes),
      reject,
      resolve,
    );
  });

  return { uploadId, storagePath, archiveName, sizeBytes: archive.size };
}

export const firebaseNexusGateway: NexusGateway = {
  fetchDashboard: () => callGoogleNexus<GoogleNexusDashboard>({ action: 'dashboard' }),
  fetchUpload: (uploadId: string) => callGoogleNexus<{ upload: GoogleNexusUpload | null }>({ action: 'getUpload', uploadId }),
  saveProject: (project: GoogleNexusProjectInput) => callGoogleNexus({ action: 'saveProject', project }),
  setProjectArchived: (projectId: string, archived: boolean) => callGoogleNexus({ action: 'setProjectArchived', projectId, archived }),
  testConnection: (projectId: string) => callGoogleNexus<GoogleNexusConnectionResult>({ action: 'testConnection', projectId }),
  syncProject: (projectId: string) => callGoogleNexus<GoogleNexusSyncResult>({ action: 'syncProject', projectId }),
  stageArchive: (uploadId, archive, onProgress) => trackPendingOperation((async () => {
    const uid = auth.currentUser?.uid;
    if (!uid) throw new Error('Sign in again before uploading.');
    return stageArchive(uid, uploadId, archive, onProgress);
  })()),
  submitValidation: (submission: GoogleNexusValidationInput) => callGoogleNexus<GoogleNexusValidationResult>({ action: 'submitValidation', submission }),
  refreshUpload: (uploadId: string) => callGoogleNexus<GoogleNexusRefreshResult>({ action: 'refreshUpload', uploadId }),
  refreshActiveUploads: () => callGoogleNexus({ action: 'refreshActiveUploads' }),
  fetchDeliveredTasks: ({ query, limit }) => callGoogleNexus<NexusDeliveredTasksResult>({ action: 'deliveredTasks', query: query ?? '', limit }),
  fetchDeliveryOverview: () => callGoogleNexus<NexusDeliveryOverview>({ action: 'deliveryOverview' }),
  fetchDeliveryBug: (bugId) => callGoogleNexus<NexusBugDetail>({ action: 'deliveryBug', bugId }),
  fetchDeliveryProject: (projectId) => callGoogleNexus<NexusProjectDetail>({ action: 'deliveryProject', projectId }),
  exportDeliveredTasks: (scope, id) => callGoogleNexus<NexusCsvExport>({ action: 'exportDeliveredTasks', scope, id }),
  fetchNexusBugs: () => callGoogleNexus<NexusBugList>({ action: 'nexusBugs' }),
  fetchNexusBug: (bugId) => callGoogleNexus<NexusBugEditor>({ action: 'nexusBug', bugId }),
  saveNexusBug: (bugId, bug) => callGoogleNexus<NexusBugMetadata>({ action: 'saveNexusBug', bugId, bug }),
  reportNexusBugCost: (bugId, report) =>
    callGoogleNexus<NexusBugReportResult>({ action: 'reportNexusBug', bugId, kind: 'cost', report }),
  reportNexusBugEstimate: (bugId, report) =>
    callGoogleNexus<NexusBugReportResult>({ action: 'reportNexusBug', bugId, kind: 'estimate', report }),
};

export { GOOGLE_NEXUS_MAX_ARCHIVE_BYTES, newGoogleNexusUploadId };
